// services/api.js
import axios from "axios";

/* ------------------ utils ------------------ */
const SIGNIN_ROUTE = "/authentication/sign-in";
const IMPERSONATION_KEY = "dev_impersonator_user_snapshot";

const normalizeBaseURL = (value = "") => {
  return String(value || "").trim().replace(/\/+$/, "");
};

export const getBaseURL = () => {
  const cra = process.env.REACT_APP_API_BASE_URL || "";
  return normalizeBaseURL(cra || "/api");
};

export const buildApiUrl = (path = "") => {
  if (!path) return "";

  const value = String(path).trim();

  // URLs que ya son completas o especiales
  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  const base = getBaseURL();

  let cleanPath = value.startsWith("/")
    ? value
    : `/${value}`;

  // Si el path ya viene con /api y el base también termina en /api,
  // quitamos el /api duplicado del path.
  if (
    base.endsWith("/api") &&
    cleanPath.startsWith("/api/")
  ) {
    cleanPath = cleanPath.substring(4);
  }

  return `${base}${cleanPath}`;
};

const getToken = () => {
  try {
    return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  } catch { return ""; }
};

const clearToken = () => {
  try {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
  } catch {}
};

let alreadyRedirecting = false;
const logoutAndRedirect = () => {
  if (alreadyRedirecting) return;
  alreadyRedirecting = true;

  clearToken();
  try { sessionStorage.removeItem(IMPERSONATION_KEY); } catch {}

  const here = typeof window !== "undefined" ? window.location.pathname : "";
  if (!here.startsWith(SIGNIN_ROUTE)) {
    try { window.location.assign(SIGNIN_ROUTE); } catch { window.location.href = SIGNIN_ROUTE; }
  } else {
    alreadyRedirecting = false;
  }
};

/* ------------------ clientes axios ------------------ */

// SOLO login
export const apiAuth = axios.create({
  baseURL: getBaseURL(),
  headers: { Accept: "application/json" }, // <- no forzar JSON
  timeout: 15000,
});

apiAuth.interceptors.request.use((config) => {
  config.baseURL = getBaseURL();
  // Para login normalmente será JSON; si es FormData no pongas Content-Type
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    delete config.headers?.["Content-Type"];
  } else {
    config.headers = { ...(config.headers || {}), "Content-Type": "application/json" };
  }
  delete config.headers?.Authorization;
  return config;
});

// Resto de llamadas
const api = axios.create({
  baseURL: getBaseURL(),
  headers: { Accept: "application/json" }, // <- no forzar JSON aquí tampoco
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  config.baseURL = getBaseURL();
  if (!config.headers) config.headers = {};

  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  else delete config.headers.Authorization;

  // 👇 MUY IMPORTANTE: si mandamos FormData, no forzar JSON
  const isFD = (typeof FormData !== "undefined") && (config.data instanceof FormData);
  if (isFD) {
    delete config.headers["Content-Type"]; // deja que el navegador ponga el boundary
  } else {
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }
  }

  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = String(err?.config?.url || "");
    const isLogin = /\/auth\/login/i.test(url);
    if (!isLogin && (status === 401 || status === 403)) {
      logoutAndRedirect();
    }
    return Promise.reject(err);
  }
);

export default api;
